import { html, TemplateResult } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_RULES_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { toggleDisabled, toggleVisibility } from '../../../../../../../../../../utils/htmlElement.utils';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../../../ui/gzChipsGeneratorInput';
import { ExchangeAntimalwareRuleFormValue } from '../../../../../../../utils/uiSettings/exchange/exchange-protection.types';
import {
  getExchangeAntimalwareActionRuleFormOptions,
  getExchangeAntimalwareAlternativeActionRuleFormOptions,
  getExchangeApplyToRuleFormOptions,
  getExchangeAntimalwareScanModeRuleFormOptions,
} from '../../../../../../../utils/uiSettings/exchange/exchange-protection.values';
import {
  ExchangeAntimalwareRuleActionType,
  ExchangeRuleScopeApplyToType,
  ExchangeRuleScopeEmailSelection,
  ExchangeAntimalwareRuleSettingsScanMode,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../../../../ui/gzRadio';
import { GzExchangeUserGroupsGridWebComponent } from '../../../../../helpers/gridComponents/userGroupsWithSelection/GzExchangeUserGroupsGridWebComponent';
import { GzHardCodedFormField } from 'apps/webcomponents/src/ui/gzForm/formField/component/GzHardCodedFormField';
import { CustomizableValidator } from '../../../../../../../../../../ui/gzForm/validators/customizable.validator';
import { ruleScopeSpecificValidators } from '../utils/validators';
import _ from 'lodash';
import { EXTENSION_VALIDATION_REGEX, validateMultipleEmails } from '../../../../../helpers/utils/validators';
import { FontSizes, FontWeights } from '../../../../../../../../../../types/common/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_RULES_FORM)
export class GzExchangeAntimalwareRulesFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ExchangeAntimalwareRuleFormValue> {
  // general section
  @query('#name')
  private ruleName: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#priority')
  private rulePriority: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#active-form-field')
  private activeFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // scope section
  @query('#rule-scope-apply-to-field')
  private ruleScopeApplyToField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-scope-from-field')
  private ruleScopeFromField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-from-specific-field')
  private ruleScopeFromSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-to-field')
  private ruleScopeToField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-to-specific-field')
  private ruleScopeToSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-match-all-recipients-field')
  private ruleScopeMatchAllRecipientsField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // settings section
  @query('#rule-settings-scan-mode-field')
  private ruleSettingsScanModeField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-settings-extensions-field')
  private ruleSettingsExtensionsField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('#rule-settings-maximum-size-enabled-field')
  private ruleSettingsMaximumSizeEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-settings-maximum-size-field')
  private ruleSettingsMaximumSizeField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#rule-settings-maximum-depth-enabled-field')
  private ruleSettingsMaximumDepthEnabledField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-settings-maximum-depth-field')
  private ruleSettingsMaximumDepthField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;
  @query('#rule-settings-scan-pua-field')
  private ruleSettingsScanPuaField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // action section
  @query('#rule-actions-infected-action-field')
  private ruleActionsInfectedActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-infected-then-field')
  private ruleActionsInfectedThenField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-suspected-action-field')
  private ruleActionsSuspectedActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-suspected-then-field')
  private ruleActionsSuspectedThenField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-unscannable-action-field')
  private ruleActionsUnscannableActionField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-actions-unscannable-then-field')
  private ruleActionsUnscannableThenField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rules-actions-notify-section')
  private ruleActionsNotifySection: HTMLElement;
  @query('#rule-actions-notify-recipient-field')
  private ruleActionsNotifyRecipientField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-sender-field')
  private ruleActionsNotifySenderField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-account-field')
  private ruleActionsNotifyAccountField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#rule-actions-notify-account-recipients-field')
  private ruleActionsNotifyAccountRecipientsField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#rule-actions-stop-if-matched-field')
  private ruleActionsStopIfMatchedField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  public constructor(private readonly maxPriority: number, value?: ExchangeAntimalwareRuleFormValue) {
    super(value);
    this.maxPriority = maxPriority;
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.ruleName.setError(error);
  }

  protected getConvertedFormValue() {
    return _.cloneDeep(this.formGroup.value);
  }

  createFormGroup(): void {
    this.ruleName.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleSettingsExtensionsField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.ruleActionsNotifyAccountRecipientsField.Validators = [
      RequiredValidator(translateFn('Required field')),
      CustomizableValidator((value: string) => validateMultipleEmails(value)),
    ];

    this.ruleScopeToSpecificField.touched = true;
    this.ruleScopeFromSpecificField.touched = true;

    this.formGroup = FormGroup.build({
      defaultPositionInGrid: this.rulePriority,
      name: this.ruleName,
      active: this.activeFormField,
      default: new GzHardCodedFormField<boolean>(false),
      scope: {
        applyTo: this.ruleScopeApplyToField,
        from: this.ruleScopeFromField,
        to: this.ruleScopeToField,
        fromSpecific: this.ruleScopeFromSpecificField,
        toSpecific: this.ruleScopeToSpecificField,
        matchAllRecipients: this.ruleScopeMatchAllRecipientsField,
      },
      settings: {
        scanMode: this.ruleSettingsScanModeField,
        extensions: this.ruleSettingsExtensionsField,
        maximumSizeEnabled: this.ruleSettingsMaximumSizeEnabledField,
        maximumSize: this.ruleSettingsMaximumSizeField,
        maximumDepthEnabled: this.ruleSettingsMaximumDepthEnabledField,
        maximumDepth: this.ruleSettingsMaximumDepthField,
        scanPUA: this.ruleSettingsScanPuaField,
      },
      actions: {
        infected: {
          action: this.ruleActionsInfectedActionField,
          then: this.ruleActionsInfectedThenField,
        },
        suspected: {
          action: this.ruleActionsSuspectedActionField,
          then: this.ruleActionsSuspectedThenField,
        },
        unscannable: {
          action: this.ruleActionsUnscannableActionField,
          then: this.ruleActionsUnscannableThenField,
        },
        stopIfMatched: this.ruleActionsStopIfMatchedField,
        notifyRecipient: this.ruleActionsNotifyRecipientField,
        notifySender: this.ruleActionsNotifySenderField,
        notifyAccount: this.ruleActionsNotifyAccountField,
        notifyAccountRecipients: this.ruleActionsNotifyAccountRecipientsField,
      },
    });
  }

  setFormGroupValue() {
    if (!this.value) {
      this.rulePriority.value = 1; // by default, a new rule will be added at the top of the list
      return;
    }

    this.formGroup.value = {
      defaultPositionInGrid: this.value.defaultPositionInGrid,
      name: this.value.name,
      active: this.value.active,
      default: this.value.default,
      scope: {
        applyTo: this.value.scope.applyTo,
        from: this.value.scope.from,
        to: this.value.scope.to,
        fromSpecific: this.value.scope.fromSpecific,
        toSpecific: this.value.scope.toSpecific,
        matchAllRecipients: this.value.scope.matchAllRecipients,
      },
      settings: {
        scanMode: this.value.settings.scanMode,
        extensions: this.value.settings.extensions,
        maximumSizeEnabled: this.value.settings.maximumSizeEnabled,
        maximumSize: this.value.settings.maximumSize,
        maximumDepthEnabled: this.value.settings.maximumDepthEnabled,
        maximumDepth: this.value.settings.maximumDepth,
        scanPUA: this.value.settings.scanPUA,
      },
      actions: {
        infected: {
          action: this.value.actions.infected.action,
          then: this.value.actions.infected.then,
        },
        suspected: {
          action: this.value.actions.suspected.action,
          then: this.value.actions.suspected.then,
        },
        unscannable: {
          action: this.value.actions.unscannable.action,
          then: this.value.actions.unscannable.then,
        },
        stopIfMatched: this.value.actions.stopIfMatched,
        notifyRecipient: this.value.actions.notifyRecipient,
        notifySender: this.value.actions.notifySender,
        notifyAccount: this.value.actions.notifyAccount,
        notifyAccountRecipients: this.value.actions.notifyAccountRecipients,
      },
    };

    // change disabled and visibility of fields based on default state
    toggleDisabled(this.ruleName, this.value.default);
    toggleDisabled(this.rulePriority, this.value.default);
    toggleDisabled(this.activeFormField, this.value.default);
    toggleDisabled(this.ruleScopeApplyToField, this.value.default);
    toggleDisabled(this.ruleScopeFromField, this.value.default);
    toggleDisabled(this.ruleScopeFromSpecificField, this.value.default);
    toggleDisabled(this.ruleScopeToField, this.value.default);
    toggleDisabled(this.ruleScopeToSpecificField, this.value.default);
    toggleDisabled(this.ruleScopeMatchAllRecipientsField, this.value.default);
    toggleVisibility(this.ruleActionsStopIfMatchedField, !this.value.default);
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <!-- General section -->
        <div id="rules-general-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle">${translateFn('General')}</gz-title>
          <gz-form-field id="name" class="label-mw-250" label="${translateFn('Rule name')}" required>
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the rule name')}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="priority" class="label-mw-250 mt-2" label="${translateFn('Set priority')}" required>
            <gz-input-number
              gz-form-input
              inputWidth="${InputWidthValues.SMALL}"
              min="${1}"
              max="${this.maxPriority}"
            ></gz-input-number>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="active-form-field" class="mt-2">
            <gz-checkbox gz-form-input label="${translateFn('Active')}" checked></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule scope section -->
        <div id="rules-scope-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Rule scope')}</gz-title>
          <gz-form-field id="rule-scope-apply-to-field" label="${translateFn('Apply to (direction)')}">
            <gz-dropdown
              gz-form-input
              .options=${getExchangeApplyToRuleFormOptions()}
              .value=${ExchangeRuleScopeApplyToType.ALL}
            ></gz-dropdown>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Senders')}"
          >
            <gz-form-field id="rule-scope-from-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.ANY}
                @radioSelectionChanged=${this.handleScopeFromEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-from-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all senders. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-from-specific-field" class="hidden">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Recipients')}"
            class="mt-2"
          >
            <gz-form-field id="rule-scope-to-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.ANY}
                @radioSelectionChanged=${this.handleScopeToEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-to-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all recipients. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-to-specific-field" class="mt-2 hidden">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="rule-scope-match-all-recipients-field" class="hidden pb-2">
            <gz-checkbox label="${translateFn('Match all recipients')}" gz-form-input></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule options section -->
        <div id="rules-settings-section">
          <gz-title id="rules-settings-title" fontWeight="500" fontSize="18" class="module-subtitle mt-2"
            >${translateFn('Options')}</gz-title
          >
          <gz-form-field id="rule-settings-scan-mode-field" label="${translateFn('Scanned file types')}">
            <gz-dropdown
              gz-form-input
              .options=${getExchangeAntimalwareScanModeRuleFormOptions()}
              .value=${ExchangeAntimalwareRuleSettingsScanMode.ALL}
              @selectionChanged="${this.handleSettingsScanModeFieldChange}"
            ></gz-dropdown>
          </gz-form-field>

          <gz-form-field class="label-mw-250 hidden" id="rule-settings-extensions-field" disabled>
            <gz-chips-generator-input
              gz-form-input
              themeInput
              validationRegex="${EXTENSION_VALIDATION_REGEX}"
              .strings=${{
                placeholder: translateFn('Specify extensions here'),
                notUniqueErrorMessage: translateFn('The entry already exists.'),
                invalidItemErrorMessage: translateFn('Invalid extension'),
              }}
            >
            </gz-chips-generator-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <div class="checkbox-with-input">
            <gz-form-field class="checkbox-width" id="rule-settings-maximum-size-enabled-field">
              <gz-checkbox
                label="${translateFn('Attachment/email body maximum size (MB)') + ':'}"
                gz-form-input
                @change="${e => toggleDisabled(this.ruleSettingsMaximumSizeField, !e.detail.checked)}"
              ></gz-checkbox>
            </gz-form-field>

            <gz-form-field id="rule-settings-maximum-size-field" class="disabled">
              <gz-input-number gz-form-input .min=${1} .max=${999} .value=${3}></gz-input-number>
            </gz-form-field>
          </div>

          <div class="checkbox-with-input">
            <gz-form-field class="checkbox-width" id="rule-settings-maximum-depth-enabled-field">
              <gz-checkbox
                label="${translateFn('Archive maximum depth (levels)') + ':'}"
                gz-form-input
                @change="${e => toggleDisabled(this.ruleSettingsMaximumDepthField, !e.detail.checked)}"
                checked
              ></gz-checkbox>
            </gz-form-field>

            <gz-form-field id="rule-settings-maximum-depth-field">
              <gz-input-number gz-form-input .min=${1} .max=${999} .value=${4}></gz-input-number>
            </gz-form-field>
          </div>

          <gz-form-field id="rule-settings-scan-pua-field">
            <gz-checkbox
              label="${translateFn('Scan for potentially unwanted applications (PUA)')}"
              gz-form-input
              checked
            ></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule actions section -->
        <div id="rules-actions-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Actions')}</gz-title>

          ${this.renderMultipleDropdownsForActionSection(
            ['rule-actions-infected-action-field', 'rule-actions-infected-then-field'],
            translateFn('Default action for infected files'),
            [ExchangeAntimalwareRuleActionType.REMEDIATE, ExchangeAntimalwareRuleActionType.MOVE_TO_QUARANTINE]
          )}
          ${this.renderMultipleDropdownsForActionSection(
            ['rule-actions-suspected-action-field', 'rule-actions-suspected-then-field'],
            translateFn('Default action for suspicious files'),
            [ExchangeAntimalwareRuleActionType.REPORT_ONLY, ExchangeAntimalwareRuleActionType.REPORT_ONLY]
          )}
          ${this.renderMultipleDropdownsForActionSection(
            ['rule-actions-unscannable-action-field', 'rule-actions-unscannable-then-field'],
            translateFn('Default action for unscannable files'),
            [ExchangeAntimalwareRuleActionType.REPORT_ONLY, ExchangeAntimalwareRuleActionType.REPORT_ONLY]
          )}

          <div id="rules-actions-notify-section" class="hidden">
            <gz-form-field id="rule-actions-notify-recipient-field" disabled>
              <gz-checkbox label="${translateFn('Notify the recipient')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-text
              id="rule-actions-notify-recipient-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn(
                'Send a notification to the intended recipients when an action is taken on an email. Emails are sent only to mailboxes on domains accepted by the company email server.'
              )}
            </gz-text>

            <gz-form-field id="rule-actions-notify-sender-field" disabled>
              <gz-checkbox label="${translateFn('Notify the sender')}" gz-form-input></gz-checkbox>
            </gz-form-field>
            <gz-text
              id="rule-actions-notify-sender-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn(
                'Send notification to the sender when an action is taken on an email. Emails are only sent to mailboxes that belong to a domain accepted by the company email server.'
              )}
            </gz-text>

            <div class="checkbox-with-input">
              <gz-form-field class="checkbox-width-320" id="rule-actions-notify-account-field" disabled>
                <gz-checkbox
                  label="${translateFn('Notify the user')}"
                  gz-form-input
                  @change="${e => this.toggleAccountRecipientsFieldDisabledState(!e.detail.checked)}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="rule-actions-notify-account-recipients-field" disabled>
                <gz-input
                  gz-form-input
                  inputWidth="${InputWidthValues.XLARGE}"
                  placeholder="${translateFn('Enter the email address')}"
                ></gz-input>
              </gz-form-field>
            </div>
            <gz-text
              id="rule-actions-notify-account-text"
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
              class="infoText helper-text"
              >${translateFn('Send notification to the specified mailboxes when an action is taken on an email.')}
            </gz-text>
          </div>

          <gz-form-field id="rule-actions-stop-if-matched-field">
            <gz-checkbox label="${translateFn('Stop processing more rules')}" gz-form-input checked></gz-checkbox>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  private renderMultipleDropdownsForActionSection(
    ids: string[],
    label: string,
    values: ExchangeAntimalwareRuleActionType[]
  ): TemplateResult {
    return html` <div class="multiple-dropdowns">
      <gz-form-field class="first-dropdown-field" id="${ids[0]}" label="${label}">
        <gz-dropdown
          gz-form-input
          .options=${getExchangeAntimalwareActionRuleFormOptions()}
          .value=${values[0]}
          @selectionChanged="${this.handleActionsNotifyVisibility}"
        ></gz-dropdown>
      </gz-form-field>

      <gz-form-field class="second-dropdown-field" id="${ids[1]}" label="${translateFn('Alternative action')}">
        <gz-dropdown
          gz-form-input
          .options=${getExchangeAntimalwareAlternativeActionRuleFormOptions()}
          .value=${values[1]}
          @selectionChanged="${this.handleActionsNotifyVisibility}"
        ></gz-dropdown>
      </gz-form-field>
    </div>`;
  }

  private handleScopeFromEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeFromSpecificField, isSpecific);
    this.ruleScopeFromSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];
  }

  private handleScopeToEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeToSpecificField, isSpecific);
    this.ruleScopeToSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];

    // show/hide match all recipients
    toggleVisibility(this.ruleScopeMatchAllRecipientsField, isSpecific);
  }

  private handleSettingsScanModeFieldChange(event: CustomEvent) {
    const scanModeTypesWithExtensions = [
      ExchangeAntimalwareRuleSettingsScanMode.USER_DEFINED_EXTENSIONS,
      ExchangeAntimalwareRuleSettingsScanMode.ALL_FILES_EXCEPT_SPECIFIC_EXTENSIONS,
    ];
    const isExtensionsFieldVisible = scanModeTypesWithExtensions.includes(event.detail.value);

    // show/hide extensions field & validate it
    toggleVisibility(this.ruleSettingsExtensionsField, isExtensionsFieldVisible);
    this.ruleSettingsExtensionsField.disabled = !isExtensionsFieldVisible;
    this.ruleSettingsExtensionsField.validate(true);
  }

  private handleActionsNotifyVisibility() {
    const currentActions = [
      this.ruleActionsInfectedActionField.value,
      this.ruleActionsSuspectedActionField.value,
      this.ruleActionsUnscannableActionField.value,
      this.ruleActionsInfectedThenField.value,
      this.ruleActionsSuspectedThenField.value,
      this.ruleActionsUnscannableThenField.value,
    ];
    const isNotifySectionVisible = currentActions.includes(ExchangeAntimalwareRuleActionType.DELETE_EMAIL);

    // show/hide notify section
    toggleVisibility(this.ruleActionsNotifySection, isNotifySectionVisible);
    // enable/disable notify fields & validate account recipients
    this.ruleActionsNotifyRecipientField.disabled = !isNotifySectionVisible;
    this.ruleActionsNotifySenderField.disabled = !isNotifySectionVisible;
    this.ruleActionsNotifyAccountField.disabled = !isNotifySectionVisible;
    this.toggleAccountRecipientsFieldDisabledState(
      !isNotifySectionVisible || !this.ruleActionsNotifyAccountField.value
    );
  }

  private toggleAccountRecipientsFieldDisabledState(disabled: boolean) {
    this.ruleActionsNotifyAccountRecipientsField.disabled = disabled;
    this.ruleActionsNotifyAccountRecipientsField.validate(true);
  }
}
